'use strict';

(function () {
	if (!customElements.get('multi-scroll-gallery')) {

		const centerStickyWrapper = (height, stickyWrapper) => {
			const availableHeight = Math.max(window.innerHeight - height, 0);
			const topOffset = Math.max(availableHeight / 2, 0);

			stickyWrapper.style.top = `${topOffset}px`;

			return topOffset;
		};

		class MultiScrollGallery extends HTMLElement {
			constructor() {
				super();

				this.activeItem = 0;
				this.observer = null;
				this.desktopInitialized = false;
				this.rafPending = false;

				this.horizontalSection = this.querySelector(
					'.multi-scroll-gallery__horizontal-section'
				);

				this.stickyWrapper = this.querySelector(
					'.multi-scroll-gallery__sticky-wrapper'
				);

				this.imgWrapper = this.querySelector(
					'.multi-scroll-gallery__right'
				);

				this.contentWrapper = this.querySelector(
					'.multi-scroll-gallery__left'
				);

				this.contentItems = this.querySelectorAll(
					'.multi-scroll-gallery__item'
				);

				this.images = this.querySelectorAll(
					'.multi-scroll-gallery__right .multi-scroll-gallery__image-wrapper'
				);

				this.imageHeight = 0;
				this.scrollDistance = 0;

				this.handleResize = this.handleResize.bind(this);
				this.handleScroll = this.handleScroll.bind(this);
			}

			connectedCallback() {
				if (
					this.contentItems.length < 2 ||
					!this.horizontalSection ||
					!this.stickyWrapper ||
					!this.imgWrapper ||
					!this.contentWrapper ||
					!this.images.length
				) {
					return;
				}

				window.addEventListener('resize', this.handleResize);
				window.addEventListener('orientationchange', this.handleResize);

				this.setupForViewport();
			}

			disconnectedCallback() {
				this.disconnectObserver();
				this.destroyDesktopScroll();

				window.removeEventListener('resize', this.handleResize);
				window.removeEventListener(
					'orientationchange',
					this.handleResize
				);
			}

			setupForViewport() {
				if (window.innerWidth >= 1200) {
					this.observeDesktopSection();
				} else {
					this.disconnectObserver();
					this.destroyDesktopScroll();
					this.setMobileLayout();
				}
			}

			observeDesktopSection() {
				if (this.desktopInitialized) {
					this.refreshDesktopLayout();
					return;
				}

				if (this.observer) return;

				if ('IntersectionObserver' in window) {
					this.observer = new IntersectionObserver(
						(entries) => {
							const shouldInit = entries.some(
								(entry) => entry.isIntersecting
							);

							if (!shouldInit) return;

							this.disconnectObserver();
							this.initDesktopScroll();
						},
						{
							rootMargin: '800px 0px',
							threshold: 0
						}
					);

					this.observer.observe(this);
				} else {
					this.initDesktopScroll();
				}
			}

			disconnectObserver() {
				if (this.observer) {
					this.observer.disconnect();
					this.observer = null;
				}
			}

			setMobileLayout() {
				this.horizontalSection.style.height = 'auto';
				this.imgWrapper.style.maxHeight = '100%';
				this.contentWrapper.style.maxHeight = '100%';
				this.stickyWrapper.style.maxHeight = '100%';
				this.stickyWrapper.style.top = '';
				this.imgWrapper.style.transform = 'translateY(0)';
			}

			initDesktopScroll() {
				if (this.desktopInitialized) return;

				this.desktopInitialized = true;

				this.refreshDesktopLayout();

				window.addEventListener('scroll', this.handleScroll, {
					passive: true
				});

				this.handleScroll();
			}

			refreshDesktopLayout() {
				if (
					window.innerWidth < 1200 ||
					!this.images.length
				) {
					return;
				}

				this.imageHeight = this.images[0].offsetHeight;

				if (!this.imageHeight) return;

				const topOffset = centerStickyWrapper(
					this.imageHeight,
					this.stickyWrapper
				);

				this.scrollDistance =
					this.imageHeight * this.images.length;

				this.horizontalSection.style.height =
					`${this.scrollDistance + topOffset}px`;

				this.imgWrapper.style.maxHeight =
					`${this.imageHeight}px`;

				this.contentWrapper.style.maxHeight =
					`${this.imageHeight}px`;

				this.stickyWrapper.style.maxHeight =
					`${this.imageHeight}px`;
			}

			destroyDesktopScroll() {
				window.removeEventListener(
					'scroll',
					this.handleScroll
				);

				this.desktopInitialized = false;
				this.rafPending = false;
			}

			handleResize() {
				if (window.innerWidth >= 1200) {
					if (this.desktopInitialized) {
						this.refreshDesktopLayout();
						this.handleScroll();
					} else {
						this.observeDesktopSection();
					}
				} else {
					this.disconnectObserver();
					this.destroyDesktopScroll();
					this.setMobileLayout();
				}
			}

			handleScroll() {
				if (
					!this.desktopInitialized ||
					window.innerWidth < 1200 ||
					this.rafPending
				) {
					return;
				}

				this.rafPending = true;

				requestAnimationFrame(() => {
					this.rafPending = false;
					this.updateScrollPosition();
				});
			}

			updateScrollPosition() {
				if (!this.imageHeight) return;

				const scrollTop = window.pageYOffset;

				const distFromTop =
					this.horizontalSection.getBoundingClientRect().top +
					window.pageYOffset;

				const scrollPosition = Math.max(
					0,
					Math.min(
						scrollTop - distFromTop,
						this.scrollDistance - this.imageHeight
					)
				);

				this.imgWrapper.style.transform =
					`translateY(-${scrollPosition}px)`;

				let nextActiveItem = this.activeItem;

				this.images.forEach((image, index) => {
					if (
						image.offsetTop <= scrollPosition &&
						scrollPosition <
							image.offsetTop + this.imageHeight
					) {
						nextActiveItem = index;
					}
				});

				if (nextActiveItem === this.activeItem) return;

				this.activeItem = nextActiveItem;

				this.contentItems.forEach((item, index) => {
					item.classList.remove('active', 'prev-item');

					if (index === this.activeItem) {
						item.classList.add('active');
					}

					if (
						this.activeItem > 0 &&
						index === this.activeItem - 1
					) {
						item.classList.add('prev-item');
					}
				});
			}
		}

		customElements.define(
			'multi-scroll-gallery',
			MultiScrollGallery
		);
	}
})();